import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import API_BASE from '../utils/api';
import axios from 'axios';
import LicenseStatus from '../components/LicenseStatus';
import useDocumentTitle from '../hooks/useDocumentTitle';

const Login = () => {
  useDocumentTitle(
    'Login | Teaching Hub School Management System',
    'Sign in to Teaching Hub to manage attendance, fees, exams, timetables, and more for your school.'
  );
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [schoolInfo, setSchoolInfo] = useState({ schoolName: '', address: '' });
  
  const navigate = useNavigate();
  const { login, user, loading: authLoading } = useAuth();
  const { theme } = useTheme();

  // Redirect if already logged in
  useEffect(() => {
    if (!authLoading && user) {
      switch(user.role) {
        case 'super_admin':
          navigate('/super-admin/dashboard');
          break;
        case 'admin':
          navigate('/admin/dashboard');
          break;
        case 'principal':
          navigate('/principal/dashboard');
          break;
        case 'class_teacher':
          navigate('/teacher/dashboard');
          break;
        case 'parent':
          navigate('/parent/dashboard');
          break;
        default:
          navigate('/');
      }
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    axios.get(`${API_BASE}/settings/public`)
      .then(res => {
        setSchoolInfo(res.data);
        if (res.data.schoolName) document.title = res.data.schoolName;
      })
      .catch(() => {});
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const result = await login(email, password);
    
    if (result.success) {
      switch(result.role) {
        case 'super_admin':
          navigate('/super-admin/dashboard');
          break;
        case 'admin':
          navigate('/admin/dashboard');
          break;
        case 'principal':
          navigate('/principal/dashboard');
          break;
        case 'class_teacher':
          navigate('/teacher/dashboard');
          break;
        case 'parent':
          navigate('/parent/dashboard');
          break;
        default:
          navigate('/');
      }
    } else {
      setError(result.error);
    }
    
    setLoading(false);
  };

  // Show loading state while auth context is initializing
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-400 via-indigo-500 to-purple-600">
        <div className="text-center">
          <div className="inline-block animate-spin mb-4">
            <div className="h-12 w-12 border-4 border-white border-r-transparent rounded-full"></div>
          </div>
          <p className="text-white font-medium">Initializing...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex relative overflow-hidden">
      {/* Animated school-themed background */}
      <div className="absolute inset-0" style={{ background: `linear-gradient(to bottom right, ${theme.loginBgFrom}, ${theme.primaryColor}, ${theme.loginBgTo})` }}>
        {/* Decorative floating shapes */}
        <div className="absolute top-10 left-10 w-72 h-72 bg-blue-400 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-pulse"></div>
        <div className="absolute top-40 right-10 w-72 h-72 bg-indigo-400 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-pulse" style={{ animationDelay: '2s' }}></div>
        <div className="absolute bottom-10 left-1/3 w-72 h-72 bg-purple-400 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-pulse" style={{ animationDelay: '4s' }}></div>
        
        {/* School pattern overlay */}
        <div className="absolute inset-0 opacity-5" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }}></div>

        {/* School icons scattered */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <span className="absolute text-4xl opacity-10 top-[10%] left-[5%]">📚</span>
          <span className="absolute text-4xl opacity-10 top-[20%] right-[10%]">🎓</span>
          <span className="absolute text-4xl opacity-10 bottom-[30%] left-[15%]">✏️</span>
          <span className="absolute text-4xl opacity-10 bottom-[15%] right-[20%]">🏫</span>
          <span className="absolute text-4xl opacity-10 top-[50%] left-[80%]">📖</span>
          <span className="absolute text-4xl opacity-10 top-[70%] left-[8%]">🔔</span>
          <span className="absolute text-3xl opacity-10 top-[5%] left-[50%]">🌟</span>
          <span className="absolute text-3xl opacity-10 bottom-[5%] right-[40%]">📐</span>
        </div>
      </div>

      {/* Left side - School branding */}
      <div className="hidden lg:flex lg:w-1/2 relative z-10 items-center justify-center p-12">
        <div className="text-center text-white max-w-md">
          <div className="mb-8">
            <div className="w-32 h-32 mx-auto rounded-full bg-white/10 backdrop-blur-sm border-2 border-white/30 flex items-center justify-center shadow-2xl overflow-hidden">
              <img src={schoolInfo.logo || "/logo.jpeg"} alt="School Logo" className="w-full h-full object-cover" />
            </div>
          </div>
          <h1 className="text-4xl font-bold mb-3 drop-shadow-lg">
            {schoolInfo.schoolName || 'School Management'}
          </h1>
          {schoolInfo.address && (
            <p className="text-blue-200 text-lg mb-6">{schoolInfo.address}</p>
          )}
          <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
            <p className="text-blue-100 text-sm leading-relaxed">
              A complete digital platform to manage students, teachers, attendance, fees, exams, and more — all in one place.
            </p>
          </div>
          <div className="mt-8 flex justify-center gap-8">
            <div className="text-center">
              <div className="text-3xl mb-1">👨‍🏫</div>
              <div className="text-xs text-blue-200">Teachers</div>
            </div>
            <div className="text-center">
              <div className="text-3xl mb-1">👥</div>
              <div className="text-xs text-blue-200">Students</div>
            </div>
            <div className="text-center">
              <div className="text-3xl mb-1">👪</div>
              <div className="text-xs text-blue-200">Parents</div>
            </div>
            <div className="text-center">
              <div className="text-3xl mb-1">📊</div>
              <div className="text-xs text-blue-200">Reports</div>
            </div>
          </div>
        </div>
      </div>

      {/* Right side - Login form */}
      <div className="flex-1 flex items-center justify-center relative z-10 p-6">
        <div className="w-full max-w-md">
          <div className="bg-white/95 backdrop-blur-xl rounded-3xl shadow-2xl p-8 border border-white/50">
            {/* Mobile logo */}
            <div className="lg:hidden flex flex-col items-center mb-6">
              <div className="w-20 h-20 rounded-full overflow-hidden shadow-lg border-4 border-indigo-100 mb-3">
                <img src={schoolInfo.logo || "/logo.jpeg"} alt="School Logo" className="w-full h-full object-cover" />
              </div>
              <h2 className="text-xl font-bold text-gray-800">
                {schoolInfo.schoolName || 'School Management'}
              </h2>
            </div>

            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-gray-800">Welcome Back</h2>
              <p className="text-gray-500 text-sm mt-1">Sign in to continue to your dashboard</p>
            </div>

            {/* License Status */}
            <div className="mb-6">
              <LicenseStatus />
            </div>
            
            <form onSubmit={handleSubmit} className="space-y-5">
              {error && (
                <div className={`border-l-4 rounded-lg px-4 py-3 text-sm ${
                  error.includes('license') || error.includes('License') || error.includes('expired') || error.includes('Expired')
                    ? 'bg-red-50 border-red-500 text-red-700'
                    : 'bg-red-50 border-red-500 text-red-700'
                }`}>
                  <div className="font-semibold mb-1">
                    {error.includes('license') || error.includes('License') || error.includes('expired') || error.includes('Expired') 
                      ? '🔐 License Issue' 
                      : '⚠️ Login Error'}
                  </div>
                  <div>{error}</div>
                  {(error.includes('license') || error.includes('License') || error.includes('expired') || error.includes('Expired')) && (
                    <div className="mt-2 text-xs opacity-90">
                      Please contact your school administrator to renew the license.
                    </div>
                  )}
                </div>
              )}
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Email Address</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">📧</span>
                  <input
                    type="email"
                    required
                    className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:border-transparent text-sm bg-gray-50 transition"
                    style={{ '--tw-ring-color': theme.primaryColor }}
                    placeholder="Enter your email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Password</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">🔒</span>
                  <input
                    type="password"
                    required
                    className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:border-transparent text-sm bg-gray-50 transition"
                    style={{ '--tw-ring-color': theme.primaryColor }}
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full py-3 text-white font-semibold rounded-xl focus:outline-none focus:ring-2 focus:ring-offset-2 transition-all disabled:opacity-50 shadow-lg"
                style={{ background: `linear-gradient(to right, ${theme.primaryColor}, ${theme.accentColor})` }}
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/></svg>
                    Signing in...
                  </span>
                ) : 'Sign In'}
              </button>
            </form>

            <div className="mt-6 text-center space-y-2">
              <a href="/forgot-password" className="text-sm font-medium" style={{ color: theme.primaryColor }}>
                Forgot Password?
              </a>
              
            </div>
          </div>

          <p className="text-center text-xs text-blue-200 mt-6">
            {schoolInfo.schoolName || 'School Management System'} &copy; {new Date().getFullYear()}
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;